import React from 'react';
import { Alert } from 'react-bootstrap';
import EptDataTable from '../custom/EptDataTable';
import AccordionBlock from './blocks/AccordionBlock';
import ButtonGroup from './blocks/ButtonGroup';
import ChildrenLayout from './blocks/ChildrenLayout';
import DetailsGridBlock from './blocks/DetailsGridBlock';
import DividerBlock from './blocks/DividerBlock';
import FeedbackInputBlock from './blocks/FeedbackInputBlock';
import MarkdownBlock from './blocks/MarkdownBlock';
import MediaListBlock from './blocks/MediaListBlock';
import MetricBlock from './blocks/MetricBlock';
import MetricCardCollection from './blocks/MetricCardCollection';
import PillListBlock from './blocks/PillListBlock';
import ProgressBarBlock from './blocks/ProgressBarBlock';
import QuoteBlock from './blocks/QuoteBlock';
import SpacerBlock from './blocks/SpacerBlock';
import ToggleButton from './blocks/ToggleButton';
import TreeWithDetailBlock from './blocks/TreeWithDetailBlock';
import ButtonList from './ButtonList';
import FormRenderer from './FormRenderer';

import { validateBlock } from '../validators';

interface Block {
  type?: string;
  kind?: string;
  label?: string;
  action?: any;
  buttons?: any[];
  icon?: any;
  content?: any;
  children?: any[];
  dataApi?: string;
  rows?: any[];
  columns?: any[];
  data_api?: string;
}

interface CardContext {
  card_id?: string;
  title?: string;
}

interface BlockRendererProps {
  block: Block;
  cardContext?: CardContext;
}

const BlockRenderer: React.FC<BlockRendererProps> = ({
  block,
  cardContext,
}) => {
  // Validate block data first
  const validation = validateBlock(block);
  if (!validation.isValid) {
    console.warn('Block validation failed:', validation.errors);
    return (
      <div className="error-boundary-block">
        <Alert variant="warning" className="mb-2">
          <Alert.Heading as="h6">⚠️ Invalid Block Data</Alert.Heading>
          <p className="mb-2 text-muted small">
            <strong>ept AI backend error.</strong> Please send an email to{' '}
            <a href="mailto:support@ept.ai">support@ept.ai</a> with a screenshot
            or the text below.
          </p>
          <p className="mb-1">
            This block has invalid data and cannot be displayed:
          </p>
          <ul className="mb-0">
            {validation.errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      </div>
    );
  }

  // Handle both "type" and "kind" fields
  const blockType = block?.type || block?.kind;

  if (!blockType) return null;

  const blockTypes = {
    pill_list: PillListBlock,
    media_list: MediaListBlock,
    quote: QuoteBlock,
    text: MarkdownBlock,
    collapsible: AccordionBlock,
    card_collection: MetricCardCollection,
    metric: MetricBlock,
    data_table: EptDataTable,
    form: FormRenderer,
    button_list: ButtonList,
    toggle_button: ToggleButton,
    progress_bar: ProgressBarBlock,
    details_grid: DetailsGridBlock,
    divider: DividerBlock,
    tree_with_detail: TreeWithDetailBlock,
    spacer: SpacerBlock,
  };

  const BlockComponent = blockTypes[blockType.toLowerCase()];

  // If not matched due to casing, try explicit check
  if (!BlockComponent && blockType.toLowerCase() === 'details_grid') {
    return <DetailsGridBlock block={block as any} />;
  }

  if (BlockComponent) {
    return <BlockComponent block={block} cardContext={cardContext} />;
  } else {
    // Handle legacy block types
    if (block.label && block.action) {
      return <FeedbackInputBlock block={block as any} />;
    }
    if (block.buttons) {
      return <ButtonGroup block={block as any} cardContext={cardContext} />;
    }
    if (block.icon && block.content) {
      return <MarkdownBlock block={block} cardContext={cardContext} />;
    }
    if (block.children) {
      return <ChildrenLayout block={block} cardContext={cardContext} />;
    }
    if (block.dataApi || block.data_api) {
      return <EptDataTable block={block as any} />;
    }
    console.warn('Unknown block type:', blockType);
    return null;
  }
};

export default BlockRenderer;
